const content = `
# Flux PhD Overview

Flux PhD is a **power system topology simulator** that quantifies the efficiency advantages of different DC-coupled and AC-coupled architectures for solar + storage + EV charging sites. It answers the question: *"How much energy do I lose in power conversions, and which topology minimizes those losses?"*

## Quick Start

1. **Browse Topologies** — Review the 12 preset power system architectures. Expand any row to see efficiency ratings, bus voltages, and conversion counts.
2. **Configure Simulation** — Select a topology, set equipment sizes (PV, BESS, chargers), choose a load profile, and run a minute-by-minute energy simulation.
3. **View Results** — Examine the energy flow chart, single-line diagram, Sankey diagram, and metric pills showing system efficiency, total energy, and peak demand.
4. **Compare Topologies** — Enable Compare Mode to select two topologies and run them with identical equipment to see side-by-side efficiency differences.

## Section Overview

| Section | Purpose |
|---------|---------|
| **Topologies** | Browse 12 preset AC/DC architectures with efficiency ratings |
| **Simulation** | Configure PV, BESS, chargers, and load profiles; run simulations |
| **Results** | Energy flow charts, Sankey diagrams, single-line diagrams, metrics |
| **Comparison** | Side-by-side topology comparison with radar and bar charts |
| **Forecasting** | ComStock-based component load forecasting pipeline |

## Key Concepts

- **Topology** — A power system architecture defining how PV, BESS, chargers, and grid connect. DC-coupled topologies share a DC bus and skip AC/DC conversions. AC-coupled topologies route everything through AC.
- **Efficiency** — Each power conversion (DC-DC, DC-AC, AC-DC) loses 1-8% of energy. Fewer conversions means higher efficiency.
- **Component Forecasting** — Predicts building load by disaggregating it into HVAC, refrigeration, plug loads, and lighting components using NREL ComStock training data.

## Who Should Use This

Flux PhD is designed for Vortx engineers and sales teams evaluating site architectures. Use it to demonstrate quantified efficiency gains of DC-coupled systems to prospective customers, or to validate topology choices during project design.
`;

export default content;
